import React from "react";
import { CalendarCheck } from "lucide-react";
import { AppCard, AppCardContent, DetailRow, DetailSection, EmptyState, StatusBadge } from "../../../ui";
import Shimmer from "../../../Common/Shimmer";
import apiClient from "../../../../utils/apiClient";
import {
  DASH, formatCurrency, formatDate, formatDuration, formatTime, formatTimeOfDay, orDash,
} from "../../../../utils/format";
import { ScorePill } from "../../../Portfolio/ResumeReport";
import EnrollmentsCard from "../EnrollmentsCard";
import RecordCardHead, { TabLink } from "../RecordCardHead";
import RecordStat from "../RecordStat";
import { useResource } from "../useStudentRecord";
import {
  ageFrom, lastPayment, nextInterviewRound, summarizeAssessments, summarizeInterviews,
} from "../studentRecord";

/* ── Learning ─────────────────────────────────────────────────────────────── */

const LearningCard = ({ stats, assessments, certificates, features, onOpen }) => {
  const s = stats.data;
  const items = [];

  if (features.codingPractice && s?.assignments) {
    const a = s.assignments;
    items.push(
      <RecordStat key="assignments" label="Coding assignments"
        value={a.totalAssignments ? `${a.attempted} of ${a.totalAssignments}` : DASH}
        hint={a.totalAssignments ? `${a.pending} not started` : "None assigned"} />,
    );
  }
  if (features.quizAssessment) {
    items.push(
      <RecordStat key="assessments" label="Assessments"
        value={assessments.avgPercent === null ? DASH : `${assessments.avgPercent}%`}
        hint={assessments.taken ? `${assessments.passed} of ${assessments.taken} passed` : "None taken"} />,
    );
  }
  if (features.codingPractice && s?.practice) {
    items.push(
      <RecordStat key="practice" label="Coding practice"
        value={s.practice.totalQuestions ? s.practice.solved : DASH}
        hint={s.practice.totalQuestions ? `solved of ${s.practice.totalQuestions} tried` : "Not started"} />,
    );
  }
  if (features.aiLearning && s?.ai) {
    items.push(
      <RecordStat key="ai" label="AI practice"
        value={s.ai.totalQuestions ? `${s.ai.avgScore}%` : DASH}
        hint={s.ai.totalQuestions ? `avg over ${s.ai.totalQuestions} answers` : "Not started"} />,
    );
  }
  if (features.aiMock && s?.mockInterview) {
    const m = s.mockInterview;
    items.push(
      <RecordStat key="mock" label="Mock interviews"
        value={m.totalInterviews ? `${m.avgScore}%` : DASH}
        hint={m.totalInterviews ? `avg of ${m.totalInterviews} · best ${m.highestScore}%` : "None taken"} />,
    );
  }
  items.push(
    <RecordStat key="certs" label="Certificates"
      value={certificates}
      hint={certificates ? "issued by the institute" : "None issued yet"} />,
  );

  return (
    <AppCard>
      <RecordCardHead title="Learning" description="Across every course they are enrolled in"
        action={<TabLink onClick={onOpen}>Performance</TabLink>} />
      <AppCardContent>
        {stats.loading && !s ? (
          <Shimmer height={96} />
        ) : (
          <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-5 md:tw-grid-cols-3">{items}</dl>
        )}
      </AppCardContent>
    </AppCard>
  );
};

/* ── Recent classes ───────────────────────────────────────────────────────── */

const RecentClassesCard = ({ studentId, onOpen }) => {
  const recent = useResource(`${studentId}:recent-classes`, () =>
    apiClient.get(`/attendance/student/${studentId}`, { params: { page: 1, limit: 5 } })
      .then((r) => r.data.sessions || []));

  return (
    <AppCard>
      <RecordCardHead title="Recent classes" description="Class sessions from the attendance log"
        action={<TabLink onClick={onOpen}>Attendance</TabLink>} />
      <AppCardContent className="tw-p-0">
        {recent.loading && !recent.data ? (
          <div className="tw-px-5 tw-pb-5"><Shimmer height={40} count={3} /></div>
        ) : !recent.data?.length ? (
          <EmptyState icon={CalendarCheck} title="No classes recorded yet"
            description="Sessions appear here once a class this student joins has ended." />
        ) : (
          <ul className="tw-m-0 tw-list-none tw-p-0">
            {recent.data.map((s) => (
              <li key={s._id}
                className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-px-5 tw-py-3 first:tw-border-t-0">
                <span className="tw-w-28 tw-shrink-0 tw-text-[0.8125rem] tw-font-medium tw-text-content">
                  {formatDate(s.sessionStart || s.sessionDate)}
                </span>
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-[0.8125rem] tw-text-content-muted">
                  {s.batchName || "Class"} · {formatTime(s.sessionStart)}
                </span>
                <span className="tw-text-xs tw-tabular-nums tw-text-content-subtle">
                  {s.activeMinutes ? formatDuration(s.activeMinutes * 60) : DASH}
                </span>
                <StatusBadge status={s.attendanceStatus} domain="attendance" />
              </li>
            ))}
          </ul>
        )}
      </AppCardContent>
    </AppCard>
  );
};

/* ── Fees ─────────────────────────────────────────────────────────────────── */

const FeesCard = ({ fees, installments, payments, onOpen }) => {
  const last = lastPayment(payments.data || []);
  return (
    <AppCard>
      <RecordCardHead title="Fees"
        action={<TabLink onClick={onOpen}>All fees</TabLink>} />
      <AppCardContent>
        <DetailSection className="tw-mb-0">
          <DetailRow label="Total fees" value={formatCurrency(fees.total)} />
          <DetailRow label="Paid" value={formatCurrency(fees.paid)} />
          <DetailRow
            label="Balance"
            value={<span className={fees.pending > 0 ? "tw-text-warning-fg" : "tw-text-success-fg"}>{formatCurrency(fees.pending)}</span>}
          />
          {installments?.overdueCount > 0 && (
            <DetailRow
              label="Overdue"
              value={(
                <span className="tw-text-danger-fg">
                  {formatCurrency(installments.overdueAmount)} · since {formatDate(installments.oldestOverdue.dueDate)}
                </span>
              )}
            />
          )}
          <DetailRow
            label="Next installment"
            value={installments?.next
              ? `${formatCurrency(installments.next.balance)} · ${formatDate(installments.next.dueDate)}`
              : "None scheduled"}
          />
          <DetailRow
            label="Last payment"
            value={last ? `${formatCurrency(last.currentPayment)} · ${formatDate(last.date || last.createdAt)}` : "None yet"}
          />
        </DetailSection>
      </AppCardContent>
    </AppCard>
  );
};

/* ── Placement ────────────────────────────────────────────────────────────── */

const PLACEMENT_WANTED = { Yes: "Wanted", No: "Not wanted" };

const PlacementCard = ({ student, portfolio, interviews, stats, features, onOpen }) => {
  const profile = portfolio.data?.portfolio;
  const analysis = portfolio.data?.latestAnalysis;
  const summary = summarizeInterviews(interviews.data || []);
  const next = nextInterviewRound(interviews.data || []);
  const mock = stats.data?.mockInterview;

  return (
    <AppCard>
      <RecordCardHead title="Placement"
        action={<TabLink onClick={onOpen}>Placement</TabLink>} />
      <AppCardContent>
        <DetailSection className="tw-mb-0">
          <DetailRow label="Placement support" value={PLACEMENT_WANTED[student.placement] || DASH} />
          <DetailRow label="Target role" value={orDash(profile?.careerProfile?.targetRole)} />
          <DetailRow
            label="Resume score"
            value={analysis?.status === "completed"
              ? <ScorePill score={analysis.overallScore} size="sm" />
              : profile?.resume?.key ? "Not analysed" : "No resume"}
          />
          {features.interviews && (
            <DetailRow
              label="Interviews"
              value={summary.total
                ? `${summary.total} · ${summary.offers} offer${summary.offers === 1 ? "" : "s"}`
                : "None yet"}
            />
          )}
          {features.interviews && next && (
            <DetailRow
              label="Next round"
              value={`${next.round} · ${next.companyName} · ${formatDate(next.date)}${next.time ? `, ${formatTimeOfDay(next.time)}` : ""}`}
            />
          )}
          {features.aiMock && (
            <DetailRow
              label="Mock interviews"
              value={mock?.totalInterviews ? `${mock.avgScore}% avg · ${mock.totalInterviews} taken` : "None taken"}
            />
          )}
        </DetailSection>
      </AppCardContent>
    </AppCard>
  );
};

/* ── Details ──────────────────────────────────────────────────────────────── */

const DetailsCard = ({ student, onOpen }) => {
  const age = ageFrom(student.dob);
  const education = [student.degree, student.passout && `passed out ${student.passout}`].filter(Boolean).join(", ");
  return (
    <AppCard>
      <RecordCardHead title="Details"
        action={<TabLink onClick={onOpen}>Full profile</TabLink>} />
      <AppCardContent>
        <DetailSection className="tw-mb-0">
          <DetailRow label="Date of birth" value={student.dob ? `${formatDate(student.dob)}${age !== null ? ` (${age})` : ""}` : DASH} />
          <DetailRow label="Gender" value={orDash(student.gender)} />
          <DetailRow label="Education" value={orDash(education)} />
          <DetailRow label="Experience" value={student.experience ? `${student.experience} yrs` : DASH} />
          <DetailRow label="Address" value={orDash(student.address)} />
          <DetailRow label="Referred by" value={orDash(student.refName)} />
        </DetailSection>
      </AppCardContent>
    </AppCard>
  );
};

/**
 * OverviewTab — the whole student on one screen: their courses and how far
 * along they are, how they are doing, what they owe, whether they are
 * placement-ready, and the facts an admin looks up most. Every card links to
 * the tab that has the full story.
 */
const OverviewTab = ({
  student, enrollments, stats, fees, installments, payments, portfolio, interviews,
  features, onOpenTab, onEdit,
}) => {
  const earned = portfolio.data?.earned;
  const assessments = summarizeAssessments(earned?.quizzes || []);

  return (
    <div className="tw-grid tw-items-start tw-gap-4 min-[1200px]:tw-grid-cols-3">
      <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4 min-[1200px]:tw-col-span-2">
        <EnrollmentsCard
          enrollments={enrollments}
          progress={stats.data?.courseCompletion}
          showFees={!!fees}
          onEdit={onEdit}
        />
        <LearningCard
          stats={stats}
          assessments={assessments}
          certificates={earned?.certificates?.length || 0}
          features={features}
          onOpen={() => onOpenTab("performance")}
        />
        <RecentClassesCard studentId={student._id} onOpen={() => onOpenTab("attendance")} />
      </div>

      <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">
        {fees && (
          <FeesCard fees={fees} installments={installments} payments={payments} onOpen={() => onOpenTab("fees")} />
        )}
        <PlacementCard
          student={student}
          portfolio={portfolio}
          interviews={interviews}
          stats={stats}
          features={features}
          onOpen={() => onOpenTab("placement")}
        />
        <DetailsCard student={student} onOpen={() => onOpenTab("profile")} />
      </div>
    </div>
  );
};

export default OverviewTab;
